// Compliance rule evaluators. Pure functions — no DB / IO.
// Mirrored on the client for UX badges, authoritative on the server.

import type { ComplianceRow, ComplianceState } from './types.js';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function daysBetween(a: Date, b: Date): number {
  return Math.floor((a.getTime() - b.getTime()) / MS_PER_DAY);
}

function isWithin(date: string | null, days: number, now: Date = new Date()): ComplianceState {
  if (!date) return 'outstanding';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return daysBetween(now, d) <= days ? 'pass' : 'outstanding';
}

export const RULES = {
  riskProfile: { name: 'Risk profile', windowDays: 5 * 365 },     // within 5 years
  factFind: { name: 'Fact find', windowDays: 3 * 365 },           // within 3 years
  snapshot: { name: 'Snapshot', windowDays: Math.floor(365 / 2) },// within 6 months
  fileNote: { name: 'File note', windowDays: 90 },                // within 90 days
  bestInterest: { name: 'Best interest', windowDays: 365 },       // within 12 months
  soaSigned: { name: 'SoA signed', windowDays: 2 * 365 },         // within 2 years
  feeConsent: { name: 'Fee consent', windowDays: 365 },           // within 12 months
} as const;

/** The seven dated checks whose window is a practice setting. */
export const RULE_KEYS = ['riskProfile', 'factFind', 'snapshot', 'fileNote', 'bestInterest', 'soaSigned', 'feeConsent'] as const;
export type RuleKey = (typeof RULE_KEYS)[number];
/** Window per check, in days. */
export type ComplianceWindows = Record<RuleKey, number>;

export const DEFAULT_WINDOWS: ComplianceWindows = Object.fromEntries(RULE_KEYS.map((k) => [k, RULES[k].windowDays])) as ComplianceWindows;

/**
 * One version of the practice's compliance windows. Versions are never edited
 * once they take effect, so the record shows which rule applied on any day.
 */
export interface ComplianceRuleVersion {
  effectiveFrom: string; // yyyy-mm-dd
  windows: ComplianceWindows;
  note?: string;
  changedBy?: string;
  changedAt?: string;
}

/** Windows in force on `today`: the latest version effective on or before it, else the defaults. */
export function windowsInEffect(versions: ComplianceRuleVersion[] | null | undefined, today: string): ComplianceWindows {
  let best: ComplianceRuleVersion | null = null;
  for (const v of versions ?? []) {
    if (v.effectiveFrom <= today && (!best || v.effectiveFrom >= best.effectiveFrom)) best = v;
  }
  return best ? { ...DEFAULT_WINDOWS, ...best.windows } : { ...DEFAULT_WINDOWS };
}

export interface ComplianceInput {
  lastRiskProfile: string | null;
  lastFactFind: string | null;
  lastSnapshot: string | null;
  lastFileNote: string | null;
  lastBestInterest: string | null;
  soaSignedDate: string | null;
  lastFeeConsent: string | null;
  updatePending: boolean;
  soaNeeded: boolean;
  roaNeeded: boolean;
  now?: Date;
}

export function evaluateCompliance(input: ComplianceInput, windows: ComplianceWindows = DEFAULT_WINDOWS): ComplianceRow {
  const now = input.now ?? new Date();
  return {
    riskProfile: isWithin(input.lastRiskProfile, windows.riskProfile, now),
    factFind: isWithin(input.lastFactFind, windows.factFind, now),
    snapshot: isWithin(input.lastSnapshot, windows.snapshot, now),
    fileNote: isWithin(input.lastFileNote, windows.fileNote, now),
    bestInterest: isWithin(input.lastBestInterest, windows.bestInterest, now),
    soaSigned: isWithin(input.soaSignedDate, windows.soaSigned, now),
    feeConsent: isWithin(input.lastFeeConsent, windows.feeConsent, now),
    updateNeeded: input.updatePending ? 'outstanding' : 'pass',
    writeSoa: input.soaNeeded ? 'outstanding' : 'pass',
    writeRoa: input.roaNeeded ? 'outstanding' : 'pass',
  };
}
